import { FusionAuthClient } from "@fusionauth/typescript-client";
import { BaseAuth, AuthResult, AuthError } from "./base-auth";
import { FUSIONAUTH_TOKEN_KEY } from "./auth-constants";
import { AUTH_CONFIG } from "./auth0-variables";
import CONFIG from "Config";
import { Utils } from "../utils";
import { clearPlatformApiSession, establishPlatformApiSessionWithCredentials } from "../../services/platform/platform-session";
import { renewCircuitBreaker } from "../../services/RenewCircuitBreaker";

export class FusionAuthAuth extends BaseAuth {
  private fusionAuthClient: FusionAuthClient;
  private applicationId: string;

  constructor() {
    super();
    const fusionAuthConfig = AUTH_CONFIG.fusionAuth || {
      serverUrl: "https://enflux-dev.fusionauth.io",
      applicationId: AUTH_CONFIG.clientId,
      apiKey: ""
    };

    this.fusionAuthClient = new FusionAuthClient(
      fusionAuthConfig.apiKey || "",
      fusionAuthConfig.serverUrl
    );
    this.applicationId = fusionAuthConfig.applicationId;
  }

  login = async (username: string, password: string, onError: (err: AuthError) => void) => {
    try {
      this.EAIClient.logLogin(username);

      const selectedSchoolId = localStorage.getItem("chosen_school");
      const resp = await this.EAIClient.logInWithCredentials(
        username,
        password,
        window.location.hostname,
        selectedSchoolId
      );

      // A fresh session must not inherit renew failures accrued before it.
      renewCircuitBreaker.reset();

      Utils.setStorageValues(resp.data.data, async () => {
        try {
          await establishPlatformApiSessionWithCredentials({
            email: username,
            password,
            schoolId: selectedSchoolId || resp.data.data?.schools?.[0]?.school_id,
          });
        } catch (error) {
          // Suppress errors from platform-api login; proceed with legacy flow.
          console.warn("Failed to initialize session in platform-api", error);
        }
        this.postLoginRedirect();
      });
    } catch (error: any) {
      // Fire-and-forget audit log; a failure here must not mask the real error.
      this.EAIClient.logBadLogin(username).catch(() => { /* best effort */ });
      onError(this.toLoginError(error));
    }
  };

  /**
   * Maps a failed `POST /login/credentials` into a user-facing {@link AuthError}.
   *
   * The API distinguishes login failures by `result.error`, not by status — a
   * locked account and a wrong password are both HTTP 401 — so the error code is
   * what we branch on. Shapes come from `handle_user_exception` in eai-api:
   *
   *   401 { result: { error: "InvalidCredentials" | "AccountLocked" | "LoginFailed",
   *                   message: "Invalid credentials Ref ID: <req_id>" } }
   *   403 { error: "SSO required" }              // bare shape, no result envelope
   *   400 "Required arguments missing"           // plain text, not JSON
   *
   * Deliberately never surfaces axios' own `error.message`: that is boilerplate
   * ("Request failed with status code 500"), not something a user can act on.
   * See EAI-5638.
   */
  private toLoginError = (error: any): AuthError => {
    // No response at all: request never reached the API.
    if (!error?.response) {
      return {
        error: "network_error",
        errorDescription: "Could not reach Enflux. Check your connection and try again."
      };
    }

    const { status, data } = error.response;
    // `data` is a plain string for some responses, so guard the property access.
    const result = typeof data === "object" && data !== null ? data.result : undefined;

    if (status === 401) {
      // A locked account must not be told its password is wrong.
      if (result?.error === "AccountLocked") {
        return {
          error: "account_locked",
          errorDescription: "Your account is locked. Please contact your administrator or support@enflux.com."
        };
      }
      return {
        error: "login_failed",
        errorDescription: "Wrong email or password"
      };
    }

    if (status === 403) {
      return {
        error: "sso_required",
        errorDescription: "This account requires SSO login"
      };
    }

    if (status === 429) {
      return {
        error: "rate_limited",
        errorDescription: "Too many sign-in attempts. Please wait a moment and try again."
      };
    }

    // Other 4xx come from handle_user_exception, so result.message is both safe
    // and actionable. 5xx messages can carry internal detail: stay generic.
    const apiMessage = status < 500 ? result?.message : undefined;
    return {
      error: "login_error",
      errorDescription: apiMessage || "Something went wrong while signing in. Please try again."
    };
  };

  loginRedirect() {
    const postRedirect = localStorage.getItem("post_login_redirect");
    if (location.pathname !== "/callback" && !location.pathname.includes("/login") && location.pathname !== postRedirect) {
      localStorage.setItem("post_login_redirect", `${location.pathname}${location.search}`);
    }
    if (location.pathname !== "/login" && location.pathname !== "/callback" && location.pathname !== "/activate" && location.pathname !== "clearstorage") {
      window.location.href = CONFIG.paths.base + "login";
    }
  }

  authorizeSSOConnection = (connection: string) => {
    // FusionAuth SSO implementation would go here
    // This would typically redirect to FusionAuth's OAuth endpoint
    const fusionAuthConfig = AUTH_CONFIG.fusionAuth || {
      serverUrl: "https://fusionauth.local.enflux.com",
      applicationId: AUTH_CONFIG.clientId,
      apiKey: ""
    };

    const fusionAuthUrl = `${fusionAuthConfig.serverUrl}/oauth2/authorize?` +
      `client_id=${this.applicationId}&` +
      "response_type=code&" +
      `redirect_uri=${encodeURIComponent(`${location.protocol}//${location.host}/callback-fusionauth`)}&` +
      "scope=email%20profile" +
      `&idp_hint=${connection}`;

    window.location.href = fusionAuthUrl;
  };

  resetPassword = async (email: string, onError: (err: AuthError) => void, onSuccess: (resp: any) => void) => {
    try {
      const response = await this.fusionAuthClient.forgotPassword({
        applicationId: this.applicationId,
        loginId: email
      });

      if (response.wasSuccessful()) {
        onSuccess({ message: "Password reset email sent" });
      } else {
        const errorResponse = response.exception;
        onError({
          error: "reset_failed",
          errorDescription: errorResponse?.message || "Password reset failed"
        });
      }
    } catch (error: any) {
      onError({
        error: "reset_error",
        errorDescription: error.message || "Password reset error occurred"
      });
    }
  };

  handleAuthentication = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const error = urlParams.get("error");
    const errorDescription = urlParams.get("error_description");

    if (error) {
      this.handleAuthError({
        error: error,
        errorDescription: errorDescription || "OAuth error"
      });
      return;
    }

    if (code) {
      await this.handleOAuthCallback(code);
    } else {
      // Check if we have a token in localStorage (for direct token authentication)
      const token = localStorage.getItem(FUSIONAUTH_TOKEN_KEY);
      if (token) {
        this.handleAuthSuccess({
          idToken: token,
          accessToken: token
        });
      } else {
        this.handleAuthError({
          error: "no_code",
          errorDescription: "No authorization code or token found"
        });
      }
    }
  };

  private getFusionAuthUserInfo = async (accessToken: string) => {
		try {
			// Get user info from FusionAuth using the access token
			const response = await this.fusionAuthClient.retrieveUserUsingJWT(accessToken);

			if (response.wasSuccessful()) {
				const user = response.response.user;
				if (user) {
					return {
						email: user.email,
						id: user.id,
						firstName: user.firstName,
						lastName: user.lastName,
					};
				}
			} else {
				console.error("Failed to get user info from FusionAuth:", response.exception);
				return null;
			}
		} catch (error) {
			console.error("Error getting user info from FusionAuth:", error);
			return null;
		}
	};

  private handleOAuthCallback = async (code: string) => {
    try {
      const response = await this.fusionAuthClient.exchangeOAuthCodeForAccessToken(
        code,
        this.applicationId,
        "", // client_secret - empty for public clients
        `${location.protocol}//${location.host}/callback-fusionauth`
      );

      if (response.wasSuccessful()) {
        const tokenResponse = response.response;
        if (tokenResponse.access_token) {
          const accessToken = tokenResponse.access_token;
          // Persist token in localStorage and cookie for the domain
          localStorage.setItem(FUSIONAUTH_TOKEN_KEY, accessToken);
          this.setFusionAuthTokenCookie(accessToken, tokenResponse.expires_in);

          const userInfo = await this.getFusionAuthUserInfo(accessToken);
          const authResult: AuthResult = {
            idToken: accessToken,
            accessToken: accessToken,
            expiresIn: tokenResponse.expires_in,
            tokenType: tokenResponse.token_type,
            email: userInfo?.email || undefined
          };
          this.handleAuthSuccess(authResult);
        } else {
          this.handleAuthError({
            error: "no_token",
            errorDescription: "No access token received"
          });
        }
      } else {
        const errorResponse = response.exception;
        this.handleAuthError({
          error: "token_exchange_failed",
          errorDescription: errorResponse?.message || "Token exchange failed"
        });
      }
    } catch (error: any) {
      this.handleAuthError({
        error: "callback_error",
        errorDescription: error.message || "OAuth callback error"
      });
    }
  };

  private setFusionAuthTokenCookie = (token: string, maxAgeSeconds?: number) => {
    const maxAge = maxAgeSeconds ?? 60 * 60 * 24; // default 24h
    const secure = window.location.protocol === "https:";
    let cookie = `${FUSIONAUTH_TOKEN_KEY}=${encodeURIComponent(token)}; path=/; max-age=${maxAge}; SameSite=Lax`;
    if (secure) cookie += "; Secure";
    document.cookie = cookie;
  };

  private clearFusionAuthTokenCookie = () => {
    document.cookie = `${FUSIONAUTH_TOKEN_KEY}=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  };

  handleLogout = (callback: () => void) => {
    // Cutover: drop the platform-api cookie too, so it cannot outlive the eai
    // session. Best-effort and not awaited, matching the legacy logout.
    void clearPlatformApiSession();

    this.EAIClient.logOut()
      .then((response) => {
        const sloUrl: string | null | undefined = response?.data?.data?.slo_redirect_url;
        Utils.clearLocalstorage();
        localStorage.removeItem(FUSIONAUTH_TOKEN_KEY);
        this.clearFusionAuthTokenCookie();
        if (sloUrl) {
          window.location.replace(sloUrl);
          return;
        }
        callback();
      })
      .catch(() => {
        Utils.clearLocalstorage();
        localStorage.removeItem(FUSIONAUTH_TOKEN_KEY);
        this.clearFusionAuthTokenCookie();
        callback();
      });
  };
}